/*
 * Copyright (C) 2019 Frontier Developments PLC.
 */

import {Component} from 'vue-property-decorator';
import {Meta} from '@/decorators';
import {MetaInfo} from 'vue-meta';
import {Action, Getter} from 'vuex-class';
import {GameInterface} from '@/store/modules/games/types';
import {IssueInterface, IssueUpdateConfigInterface} from '@/store/modules/issues/types';
import {LoadingState, LoadingStateInterface} from '@/components/Loading/types';
import Loading from '@/components/Loading/index.vue';
import DetailsList from '@/components/DetailsList/scripts';
import ImageList from '@/components/ImageList/scripts';
import Captcha from '@/mixins/Captcha';
import {mixins} from 'vue-class-component';
// he type definitions from @types/he: https://github.com/DefinitelyTyped/DefinitelyTyped/blob/master/types/he
import he from 'he';


/**s
 * Edit Issue
 *
 *  * @author jgeorgiadou@frontier.co.uk
 */

@Component({
    name: 'v-edit-issue',
    components: {
        'c-loading': Loading,
        'c-details-list': DetailsList,
        'c-image-list': ImageList,
    },
})

export default class EditIssue extends mixins(Captcha) {
    // Page Title
    @Action('updatePageTitle', {namespace: 'settingsModule'})
    public updatePageTitle!: (title: string) => void;
    // Update current view name in state
    @Action('updateCurrentView', {namespace: 'userSessionModule'})
    public updateCurrentView!: (view: { name: string, params?: any } | undefined) => void;
    // Settings masthead
    @Action('updateMastheadImage', {namespace: 'settingsModule'})
    public updateMastheadImage!: (imageSlug: string | undefined) => void;
    // Games
    @Action('fetchGames', {namespace: 'gamesStateModule'})
    public fetchGames!: () => Promise<void>;
    @Getter('getGames', {namespace: 'gamesStateModule'})
    public getGames!: GameInterface[];
    // Issue
    @Action('fetchIssue', {namespace: 'issuesStateModule'})
    public fetchIssue!: (issueId: number) => Promise<void>;
    @Action('updateIssue', {namespace: 'issuesStateModule'})
    public updateIssue!: (issueConfig: IssueUpdateConfigInterface) => Promise<boolean>;
    @Getter('getCurrentIssue', {namespace: 'issuesStateModule'})
    public issue!: IssueInterface;

    public state: LoadingStateInterface = new LoadingState(false, false);
    // loading for the submit button
    public loading: boolean = false;
    public numberOfStepsToReproduce: number = 1;
    public reproductionStepsArray: any[] = [''];

    /**
     * Mounted life-cycle event.
     *
     * Initialises the loader.
     */
    public async mounted(): Promise<void> {

        this.reloadRecaptcha();

        // Update current view name in state
        this.updateCurrentView({
            name: this.$router.currentRoute.name || '/',
            params: this.$router.currentRoute.params,
        });
        // Set the page title
        this.updatePageTitle(this.$i18n.translate('view.editIssue.title', {}) || '');

        try {
            this.state.start();
            await this.fetchIssue(+this.$route.params.id);

            // manually decoding fields
            this.issue.title = he.decode(this.issue.title);
            if (typeof this.issue.issueDetails !== 'undefined' &&
                typeof this.issue.issueDetails.description !== 'undefined' &&
                this.issue.issueDetails.description !== ''
            ) {
                this.issue.issueDetails.description = he.decode(this.issue.issueDetails.description);
            }

            // Update Masthead Image
            await this.fetchGames();
            const game: GameInterface = this.getGames.find((gameItem: GameInterface): boolean => {
                return this.issue.game === gameItem.name;
            }) as GameInterface;
            if (game) {
                this.updateMastheadImage(game.key_art);
            }

            if (typeof this.issue.issueDetails !== 'undefined') {
                // separate the one step to individual steps
                const stepsToReproduce: string = this.issue.issueDetails.stepsToReproduce.split('&lt;/p&gt;').join('');
                const storedReproductionSteps: string[] = stepsToReproduce.split('&lt;p&gt;');
                if (stepsToReproduce) {
                    let i: number = 1;
                    for (const eachStoredStep of storedReproductionSteps) {
                        if (eachStoredStep !== '') {
                            this.reproductionStepsArray[i] = he.decode(eachStoredStep);
                            i++;
                        }
                    }
                    this.numberOfStepsToReproduce = this.reproductionStepsArray.length - 1;
                }
            }

            this.state.complete();
        } catch (e) {
            this.state.failed(e.message);
        }
    }

    /**
     * Destroyed lifecycle hook
     */
    public destroyed(): void {
        this.updateMastheadImage(undefined);
    }

    /**
     * Recaptcha verification.
     * @param {string} token
     * @return {Promise<void>}
     */
    public async onVerify(token: string): Promise<void> {
        if (this.issue.issueDetails) {
            this.loading = true;

            let $combinedSteps: string = '';
            if (this.reproductionStepsArray.length >= 1) {
                for (const n of this.reproductionStepsArray) {
                    if (n !== '') {
                        $combinedSteps = $combinedSteps + '<p>' + n + '</p>';
                    }
                }
            }

            const issueConfig: IssueUpdateConfigInterface = {
                issueId: this.issue.id,
                issueDetailsIdString: this.issue.issueDetails.idString,
                titleData: {
                    title: this.issue.title as string,
                },
                detailsData: {
                    description: this.issue.issueDetails.description,
                    how_to_reproduce: $combinedSteps,
                    time_seen: this.issue.issueDetails.timeSeen,
                },
                captcha_token: token,
            };
            const resultSuccess: boolean = await this.updateIssue(issueConfig);
            if (true === resultSuccess) {
                this.$toastr.success(this.$i18n.translate('view.editIssue.updateIssue.success', {}) || '');
                this.loading = false;
                this.$router.push({name: 'issue-detail', params: {id: JSON.stringify(this.issue.id)}});
            }
            if (false === resultSuccess) {
                this.$toastr.error(this.$i18n.translate('view.editIssue.updateIssue.error', {}) || '');
                this.loading = false;
            }
        }
    }

    /**
     * Edit the form
     *
     * @return {Promise<void>}
     */
    public async submitForm(): Promise<void> {
        this.$validator.validateAll().then(async (result: boolean) => {
            if (result) {
                // Update issue via recaptcha
                await this.submit();
            }
        });
    }

    /**
     * Cancel
     *
     */
    public cancel(): void {
        this.$router.push({name: 'my-issues'});
    }

    /**
     * Meta generation.
     *
     *
     * This is an example cascaded meta generator for title and other met properties.
     *
     * @returns {MetaInfo}
     */
    @Meta
    public meta(): MetaInfo {
        const title: string = 'Edit Issue';

        return {
            title,
            meta: [
                {
                    property: 'og:title',
                    content: `${this.$i18n.translate('application.title', {}) || ''} - ${title}`,
                },
                {
                    property: 'og:description',
                    content: 'Edit your issue.',
                },
                {
                    property: 'og:image',
                    content: window.location.hostname + '/img/og_image.jpg',
                },
            ],
        };
    }
}
